import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormikProps } from 'formik';
import './CreateDeal.scss';

import { useAuthContext } from 'contexts/AuthContext';
import { NSButton } from 'bricks';
import PageTitle from 'components/PageTitle/PageTitle';
import Map from 'components/Map/Map';
import DealForm from 'components/DealForm/DealForm';
import { IFormValues } from 'components/DealForm/types';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { useGetDealById } from '../hooks/useGetDealById';

const CreateDeal = () => {
    const { selectedOrganizationId } = useAuthContext();
    const { dealId } = useParams<{ dealId: string }>();

    const { data: fetchedDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const [mapCenter, setMapCenter] = useState(
        fetchedDeal?.latitude && fetchedDeal?.longitude
            ? { lat: fetchedDeal.latitude, lng: fetchedDeal.longitude }
            : DEFAULT_NYC_COORDINATES,
    );

    const formRef = useRef<FormikProps<IFormValues>>(null);
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditMode = dealId !== 'new' && !Number.isNaN(Number(dealId));

    useEffect(() => {
        if (fetchedDeal?.latitude && fetchedDeal?.longitude) {
            setMapCenter({ lat: fetchedDeal.latitude, lng: fetchedDeal.longitude });
        }
    }, [fetchedDeal]);

    const handleFinishClick = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleSubmittingChange = (submitting: boolean) => {
        setIsSubmitting(submitting);
    };

    return (
        <div>
            <PageTitle
                title={isEditMode ? 'Edit deal details' : 'Create deal'}
                subtitle={isEditMode ? 'Modify existing information about this deal.' : 'Fill out the information below to populate this deal.'}
                beta
                betaCustomText="Beta"
            >
                <NSButton color="secondary" callback={handleGoBack} className="mr-2 heap-deal-pipeline-create-exit" text="Exit" allowReadOnlyAccess />
                <NSButton
                    color="primary"
                    callback={handleFinishClick}
                    className={isEditMode ? 'heap-deal-dashboard-save-edit-details' : 'heap-deal-pipeline-create-finish'}
                    text={isEditMode ? 'Save' : 'Finish'}
                    data-testid="finish-button"
                    isLoading={isSubmitting}
                />
            </PageTitle>
            <div className="d-flex mt-2">
                <DealForm ref={formRef} onMapCenterChange={setMapCenter} initialValues={fetchedDeal} onSubmitting={handleSubmittingChange} />

                <div className="CreateDeal__map-column w-50">
                    <Map
                        lng={mapCenter.lng}
                        lat={mapCenter.lat}
                        zoom={10}
                        hideMarker={!isEditMode}
                        height="100%"
                        autoResize
                    />
                </div>
            </div>
        </div>
    );
};

export default CreateDeal;
